// /src/lib/authStorage.js
// IndexedDB + localStorage storage adapter for Supabase auth persistence.
// Android Chrome/PWA can clear localStorage when the app is swiped away or
// the OS reclaims memory.  IndexedDB is more durable — this adapter writes
// to both, reads IndexedDB first, and falls back to localStorage silently.

const DB_NAME = 'ournet-auth';
const STORE_NAME = 'session';
const DB_VERSION = 1;

// Cache the open DB handle so we don't re-open on every call
let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => resolve(request.result);

      request.onerror = () => {
        console.warn('authStorage: IndexedDB open failed, falling back to localStorage');
        dbPromise = null; // allow retry
        reject(request.error);
      };
    } catch (e) {
      // indexedDB may not exist at all (e.g. some privacy modes)
      dbPromise = null;
      reject(e);
    }
  });

  return dbPromise;
}

function idbGet(key) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
      }),
    // openDB rejected — IndexedDB unavailable
    () => null
  );
}

function idbSet(key, value) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.put(value, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
    // openDB rejected — silently skip
    () => {}
  );
}

function idbRemove(key) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.delete(key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
    // openDB rejected — silently skip
    () => {}
  );
}

/**
 * Supabase-compatible storage adapter.
 * Implements getItem / setItem / removeItem (all async — Supabase accepts Promises).
 * Writes to both IndexedDB and localStorage.
 * Reads from IndexedDB first; falls back to localStorage.
 */
export const authStorage = {
  async getItem(key) {
    try {
      // Try IndexedDB first (survives Android app swipe)
      const idbValue = await idbGet(key);
      if (idbValue !== null && idbValue !== undefined) {
        // Sync back to localStorage in case it was cleared
        try { localStorage.setItem(key, idbValue); } catch (_) { /* quota */ }
        return idbValue;
      }
    } catch (_) {
      // IndexedDB failed — fall through
    }

    // Fall back to localStorage
    try {
      return localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  },

  async setItem(key, value) {
    // Write to both stores so either can serve as source of truth
    try { localStorage.setItem(key, value); } catch (_) { /* quota */ }
    try { await idbSet(key, value); } catch (_) { /* IDB unavailable */ }
  },

  async removeItem(key) {
    try { localStorage.removeItem(key); } catch (_) { /* ignore */ }
    try { await idbRemove(key); } catch (_) { /* IDB unavailable */ }
  }
};
